/**
 * RecurringExpensesPage Component
 * Manage recurring expense templates: create, edit, pause/resume/cancel.
 * Route: /recurring-expenses
 */

import React, { useState, useEffect, useCallback } from 'react';
import { recurringExpensesApi, expenseCategoriesApi, listProperties } from '../lib/api';
import { PageHeader } from '../components/layout/index';
import { ExpensesLayout } from '../components/expenses/ExpensesLayout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Modal } from '../components/Modal';
import { HelpBlock, HelpTooltip } from '../components/ui/HelpTooltip';
import type {
  RecurringExpense,
  RecurringExpenseDetail,
  CreateRecurringExpenseBody,
  UpdateRecurringExpenseBody,
  ExpenseCategory,
} from '../types/expense.types';
import type { PropertyWithStats } from '../lib/api';
import { formatAmount, formatExpenseDate, parseDollarsToCents, centsToDollarString } from '../utils/expense.utils';

const FREQUENCY_OPTIONS: { value: RecurringExpense['frequency']; label: string }[] = [
  { value: 'WEEKLY', label: 'Weekly' },
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'QUARTERLY', label: 'Quarterly' },
  { value: 'ANNUALLY', label: 'Annually' },
];

function StatusBadge({ status, id }: { status: RecurringExpense['status']; id: string }) {
  if (status === 'ACTIVE') return <Badge variant="secondary" className="bg-success/10 text-success" data-testid={`recurring-status-${id}`}>Active</Badge>;
  if (status === 'PAUSED') return <Badge variant="secondary" className="bg-warning/10 text-warning" data-testid={`recurring-status-${id}`}>Paused</Badge>;
  return <Badge variant="secondary" data-testid={`recurring-status-${id}`}>Cancelled</Badge>;
}

interface RecurringFormState {
  scope: 'PROPERTY' | 'UNIT';
  propertyId: string;
  unitId: string;
  amountDisplay: string;
  description: string;
  vendorName: string;
  irsCategoryId: string;
  frequency: RecurringExpense['frequency'];
  startDate: string;
  endDate: string;
}

function buildInitialFormState(propertyId: string): RecurringFormState {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return {
    scope: 'PROPERTY',
    propertyId,
    unitId: '',
    amountDisplay: '',
    description: '',
    vendorName: '',
    irsCategoryId: '',
    frequency: 'MONTHLY',
    startDate: `${year}-${month}-${day}`,
    endDate: '',
  };
}

function buildFormFromTemplate(template: RecurringExpense): RecurringFormState {
  return {
    scope: template.scope,
    propertyId: template.propertyId,
    unitId: template.unitId ?? '',
    amountDisplay: centsToDollarString(template.amount),
    description: template.description,
    vendorName: template.vendorName ?? '',
    irsCategoryId: template.irsCategoryId ?? '',
    frequency: template.frequency,
    startDate: template.startDate,
    endDate: template.endDate ?? '',
  };
}

interface RecurringFormProps {
  form: RecurringFormState;
  onChange: (updates: Partial<RecurringFormState>) => void;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
  isLoading: boolean;
  categories: ExpenseCategory[];
  mode: 'create' | 'edit';
}

function RecurringExpenseForm({
  form,
  onChange,
  onSubmit,
  onCancel,
  isLoading,
  categories,
  mode,
}: RecurringFormProps) {
  const sortedCategories = [...categories].sort((a, b) => a.sortOrder - b.sortOrder);

  return (
    <form data-testid="recurring-form" onSubmit={onSubmit} className="flex flex-col gap-4">
      <div>
        <label
          htmlFor="re-scope"
          className="block text-xs font-medium text-muted-foreground mb-1"
        >
          Scope
        </label>
        <select
          id="re-scope"
          value={form.scope}
          onChange={(e) => onChange({ scope: e.target.value as 'PROPERTY' | 'UNIT' })}
          className="w-full px-3 py-2 text-sm border border-input rounded-md bg-background text-foreground outline-none min-h-[44px]"
          data-testid="recurring-form-scope"
          aria-required="true"
          disabled={mode === 'edit'}
        >
          <option value="PROPERTY">Property</option>
          <option value="UNIT">Unit</option>
        </select>
      </div>

      {form.scope === 'UNIT' && (
        <div>
          <label
            htmlFor="re-unit-id"
            className="block text-xs font-medium text-muted-foreground mb-1"
          >
            Unit ID
          </label>
          <input
            id="re-unit-id"
            type="text"
            value={form.unitId}
            onChange={(e) => onChange({ unitId: e.target.value })}
            placeholder="Enter unit ID"
            className="w-full px-3 py-2 text-sm border border-input rounded-md bg-background text-foreground outline-none min-h-[44px]"
            data-testid="recurring-form-unit-id"
            aria-required="true"
            disabled={mode === 'edit'}
          />
        </div>
      )}

      <div>
        <label
          htmlFor="re-amount"
          className="block text-xs font-medium text-muted-foreground mb-1"
        >
          Amount ($)
        </label>
        <input
          id="re-amount"
          type="number"
          min="0"
          step="0.01"
          value={form.amountDisplay}
          onChange={(e) => onChange({ amountDisplay: e.target.value })}
          placeholder="0.00"
          className="w-full px-3 py-2 text-sm border border-input rounded-md bg-background text-foreground outline-none min-h-[44px]"
          data-testid="recurring-form-amount"
          aria-required="true"
          required
        />
      </div>

      <div>
        <label
          htmlFor="re-description"
          className="block text-xs font-medium text-muted-foreground mb-1"
        >
          Description
        </label>
        <input
          id="re-description"
          type="text"
          value={form.description}
          onChange={(e) => onChange({ description: e.target.value })}
          placeholder="e.g. Monthly water bill"
          className="w-full px-3 py-2 text-sm border border-input rounded-md bg-background text-foreground outline-none min-h-[44px]"
          data-testid="recurring-form-description"
          aria-required="true"
          required
        />
      </div>

      <div>
        <label
          htmlFor="re-vendor"
          className="block text-xs font-medium text-muted-foreground mb-1"
        >
          Vendor Name (optional)
        </label>
        <input
          id="re-vendor"
          type="text"
          value={form.vendorName}
          onChange={(e) => onChange({ vendorName: e.target.value })}
          placeholder="e.g. City Water Utility"
          className="w-full px-3 py-2 text-sm border border-input rounded-md bg-background text-foreground outline-none min-h-[44px]"
          data-testid="recurring-form-vendor"
        />
      </div>

      <div>
        <label
          htmlFor="re-category"
          className="block text-xs font-medium text-muted-foreground mb-1"
        >
          IRS Category (optional)
        </label>
        <select
          id="re-category"
          value={form.irsCategoryId}
          onChange={(e) => onChange({ irsCategoryId: e.target.value })}
          className="w-full px-3 py-2 text-sm border border-input rounded-md bg-background text-foreground outline-none min-h-[44px]"
          data-testid="recurring-form-category"
        >
          <option value="">-- Uncategorized --</option>
          {sortedCategories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
              {c.scheduleELine ? ` (Schedule E: ${c.scheduleELine})` : ''}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label
          htmlFor="re-frequency"
          className="block text-xs font-medium text-muted-foreground mb-1"
        >
          Frequency
        </label>
        <select
          id="re-frequency"
          value={form.frequency}
          onChange={(e) => onChange({ frequency: e.target.value as RecurringExpense['frequency'] })}
          className="w-full px-3 py-2 text-sm border border-input rounded-md bg-background text-foreground outline-none min-h-[44px]"
          data-testid="recurring-form-frequency"
          aria-required="true"
          disabled={mode === 'edit'}
        >
          {FREQUENCY_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label
          htmlFor="re-start-date"
          className="block text-xs font-medium text-muted-foreground mb-1"
        >
          Start Date
        </label>
        <input
          id="re-start-date"
          type="date"
          value={form.startDate}
          onChange={(e) => onChange({ startDate: e.target.value })}
          className="w-full px-3 py-2 text-sm border border-input rounded-md bg-background text-foreground outline-none min-h-[44px]"
          data-testid="recurring-form-start-date"
          aria-required="true"
          required
          disabled={mode === 'edit'}
        />
      </div>

      <div>
        <label
          htmlFor="re-end-date"
          className="block text-xs font-medium text-muted-foreground mb-1"
        >
          End Date (optional)
        </label>
        <input
          id="re-end-date"
          type="date"
          value={form.endDate}
          onChange={(e) => onChange({ endDate: e.target.value })}
          className="w-full px-3 py-2 text-sm border border-input rounded-md bg-background text-foreground outline-none min-h-[44px]"
          data-testid="recurring-form-end-date"
        />
      </div>

      <div className="flex gap-3 justify-end">
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={isLoading} data-testid="recurring-form-submit">
          {isLoading ? 'Saving...' : mode === 'create' ? 'Create Template' : 'Save Changes'}
        </Button>
      </div>
    </form>
  );
}

interface ExpandedRowProps {
  templateId: string;
}

function ExpandedTemplateRow({ templateId }: ExpandedRowProps) {
  const [detail, setDetail] = useState<RecurringExpenseDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function load() {
      try {
        setLoading(true);
        const result = await recurringExpensesApi.get(templateId);
        setDetail(result);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load history');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [templateId]);

  if (loading) return <div className="p-4 text-xs text-muted-foreground">Loading history...</div>;
  if (error) return <div className="p-4 text-xs text-destructive">{error}</div>;
  if (!detail || detail.generatedExpenses.length === 0) {
    return <div className="p-4 text-xs text-muted-foreground">No expenses generated yet.</div>;
  }

  return (
    <div className="py-2 px-4 pb-4">
      <p className="m-0 mb-2 text-xs font-semibold text-foreground">
        Generated Expenses ({detail.generatedExpenses.length})
      </p>
      <table className="w-full border-collapse text-xs">
        <thead>
          <tr>
            {['Date', 'Description', 'Amount'].map((col) => (
              <th
                key={col}
                className={`px-2 py-1 border-b border-border text-xs font-semibold text-muted-foreground uppercase ${col === 'Amount' ? 'text-right' : 'text-left'}`}
              >
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {detail.generatedExpenses.map((e) => (
            <tr key={e.id} className="border-b border-border">
              <td className="px-2 py-1 text-muted-foreground">
                {formatExpenseDate(e.expenseDate)}
              </td>
              <td className="px-2 py-1 text-foreground">
                {e.description}
              </td>
              <td className="px-2 py-1 text-right font-semibold text-foreground">
                {formatAmount(e.amount)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function RecurringExpensesPage() {
  const [properties, setProperties] = useState<PropertyWithStats[]>([]);
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [templates, setTemplates] = useState<RecurringExpense[]>([]);
  const [propertiesLoading, setPropertiesLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [selectedPropertyId, setSelectedPropertyId] = useState('');

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<RecurringExpense | null>(null);
  const [formState, setFormState] = useState<RecurringFormState>(() => buildInitialFormState(''));
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  useEffect(() => {
    async function loadBootstrap() {
      setPropertiesLoading(true);
      try {
        const [propertiesResult, categoriesResult] = await Promise.all([
          listProperties(),
          expenseCategoriesApi.list(),
        ]);
        setProperties(propertiesResult.properties);
        setCategories(categoriesResult.categories);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load properties');
      } finally {
        setPropertiesLoading(false);
      }
    }
    loadBootstrap();
  }, []);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await recurringExpensesApi.list(selectedPropertyId || undefined);
      setTemplates(result.templates);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load recurring expenses');
    } finally {
      setLoading(false);
    }
  }, [selectedPropertyId]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const handleOpenCreate = () => {
    setEditingTemplate(null);
    setFormState(buildInitialFormState(selectedPropertyId));
    setFormError(null);
    setIsFormOpen(true);
  };

  const handleOpenEdit = (template: RecurringExpense) => {
    setEditingTemplate(template);
    setFormState(buildFormFromTemplate(template));
    setFormError(null);
    setIsFormOpen(true);
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormSubmitting(true);
    setFormError(null);
    try {
      const amount = parseDollarsToCents(formState.amountDisplay);
      if (editingTemplate) {
        const body: UpdateRecurringExpenseBody = {
          amount,
          description: formState.description,
          vendorName: formState.vendorName || undefined,
          irsCategoryId: formState.irsCategoryId || undefined,
          endDate: formState.endDate || null,
        };
        const updated = await recurringExpensesApi.update(editingTemplate.id, body);
        setTemplates((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
      } else {
        const body: CreateRecurringExpenseBody = {
          scope: formState.scope,
          propertyId: formState.propertyId,
          unitId: formState.scope === 'UNIT' && formState.unitId ? formState.unitId : undefined,
          amount,
          description: formState.description,
          vendorName: formState.vendorName || undefined,
          irsCategoryId: formState.irsCategoryId || undefined,
          frequency: formState.frequency,
          startDate: formState.startDate,
          endDate: formState.endDate || undefined,
        };
        const created = await recurringExpensesApi.create(body);
        setTemplates((prev) => [...prev, created]);
      }
      setIsFormOpen(false);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to save template');
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleAction = async (id: string, action: 'pause' | 'resume' | 'cancel') => {
    setActionLoading(`${id}-${action}`);
    try {
      const updated = await recurringExpensesApi[action](id);
      setTemplates((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to ${action} template`);
    } finally {
      setActionLoading(null);
    }
  };

  if (propertiesLoading) {
    return (
      <div className="max-w-4xl mx-auto pb-6">
        <div className="px-[var(--page-padding-x)] pt-4">
          <Skeleton className="h-6 w-48" />
        </div>
        <div className="px-[var(--page-padding-x)] mt-6 space-y-4">
          <Skeleton className="h-16 w-full rounded-lg" />
          <Skeleton className="h-64 w-full rounded-lg" />
        </div>
      </div>
    );
  }

  return (
    <ExpensesLayout>
    <div data-testid="recurring-expenses-page">
      {/* Header */}
      <div className="px-[var(--page-padding-x)]">
        <PageHeader
          title="Recurring Expenses"
          action={
            <Button size="sm" onClick={handleOpenCreate} disabled={!selectedPropertyId} data-testid="recurring-create-button" aria-label="Create recurring expense template">
              + Create Template
            </Button>
          }
        />
      </div>

      <div className="px-[var(--page-padding-x)] space-y-5">
        {/* Help block */}
        <HelpBlock
          summary="Set up templates for expenses that repeat on a schedule, like utility bills or lawn care. The system automatically creates a confirmed expense entry for you each period."
          details={"How it works: Create a template with the amount, description, and frequency (weekly, monthly, quarterly, or annually). Starting from your chosen date, the system generates a new expense entry each period automatically.\n\nStatus meanings:\n\u2022 Active \u2014 generating expenses on schedule\n\u2022 Paused \u2014 temporarily stopped, can be resumed\n\u2022 Cancelled \u2014 permanently stopped, no more expenses will be generated\n\nGenerated expenses are automatically confirmed and appear directly in your expense list and reports \u2014 no review needed."}
        />

        {/* Property Selector */}
        <div className="rounded-lg border border-border bg-card p-4">
          <label
            htmlFor="recurring-property-select"
            className="block text-xs font-medium text-muted-foreground mb-1"
          >
            Property
          </label>
          <select
            id="recurring-property-select"
            value={selectedPropertyId}
            onChange={(e) => setSelectedPropertyId(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-input rounded-md bg-background text-foreground outline-none min-h-[44px]"
            data-testid="recurring-property-filter"
            aria-label="Select property"
          >
            <option value="">All Properties</option>
            {properties.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>

        {/* Error */}
        {error && (
          <div
            role="alert"
            className="p-3 bg-destructive/5 border border-destructive/20 rounded-md text-destructive text-sm"
          >
            {error}
          </div>
        )}

        {/* Templates Table */}
        <div className="rounded-lg border border-border bg-card p-4" data-testid="recurring-expenses-table">
          {loading ? (
            <div className="py-8 text-center text-muted-foreground">
              Loading templates...
            </div>
          ) : templates.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              No recurring expense templates yet.{' '}
              <Button type="button" variant="link" size="sm" onClick={handleOpenCreate}>
                Create one
              </Button>
            </div>
          ) : (
            <>
            {/* Mobile: card list */}
            <div className="flex flex-col md:hidden -mx-4 -my-4">
              {templates.map((template) => (
                <div key={template.id} className="border-b border-border last:border-b-0">
                  <div
                    className="flex items-start justify-between gap-3 px-4 py-3 cursor-pointer"
                    onClick={() => setExpandedId((prev) => (prev === template.id ? null : template.id))}
                    role="button"
                    tabIndex={0}
                    aria-expanded={expandedId === template.id}
                    aria-label={`${template.description} — ${expandedId === template.id ? 'collapse' : 'expand'} history`}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setExpandedId((prev) => (prev === template.id ? null : template.id));
                      }
                    }}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-foreground truncate">{template.description}</span>
                        <StatusBadge status={template.status} id={`mobile-${template.id}`} />
                      </div>
                      {template.vendorName && (
                        <span className="text-xs text-muted-foreground">{template.vendorName}</span>
                      )}
                      <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground flex-wrap">
                        <span>{FREQUENCY_OPTIONS.find((o) => o.value === template.frequency)?.label ?? template.frequency}</span>
                        <span>·</span>
                        <span>Next: {formatExpenseDate(template.nextRunDate)}</span>
                      </div>
                    </div>
                    <div className="shrink-0 text-right">
                      <span className="text-sm font-semibold text-foreground tabular-nums">{formatAmount(template.amount)}</span>
                      <span className="text-xs text-muted-foreground block mt-0.5">{expandedId === template.id ? '▲' : '▼'}</span>
                    </div>
                  </div>
                  {/* Mobile actions */}
                  {template.status !== 'CANCELLED' && (
                    <div className="flex gap-2 px-4 pb-3" onClick={(e) => e.stopPropagation()}>
                      <Button type="button" variant="outline" size="sm" onClick={() => handleOpenEdit(template)} aria-label={`Edit ${template.description}`}>
                        Edit
                      </Button>
                      {template.status === 'ACTIVE' && (
                        <Button type="button" variant="outline" size="sm" onClick={() => handleAction(template.id, 'pause')} disabled={actionLoading === `${template.id}-pause`} aria-label={`Pause ${template.description}`}>
                          {actionLoading === `${template.id}-pause` ? '...' : 'Pause'}
                        </Button>
                      )}
                      {template.status === 'PAUSED' && (
                        <Button type="button" size="sm" onClick={() => handleAction(template.id, 'resume')} disabled={actionLoading === `${template.id}-resume`} aria-label={`Resume ${template.description}`}>
                          {actionLoading === `${template.id}-resume` ? '...' : 'Resume'}
                        </Button>
                      )}
                      <Button type="button" variant="destructive" size="sm" onClick={() => handleAction(template.id, 'cancel')} disabled={actionLoading === `${template.id}-cancel`} aria-label={`Cancel ${template.description}`}>
                        {actionLoading === `${template.id}-cancel` ? '...' : 'Cancel'}
                      </Button>
                    </div>
                  )}
                  {/* Mobile expanded history */}
                  {expandedId === template.id && (
                    <div className="border-t border-border bg-accent/30">
                      <ExpandedTemplateRow templateId={template.id} />
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Desktop: table */}
            <div className="hidden md:block overflow-x-auto">
              <table
                className="w-full border-collapse text-sm"
                aria-label="Recurring expense templates"
              >
                <thead>
                  <tr>
                    {['Description', 'Amount', 'Frequency', 'Next Run', 'Status', 'Actions'].map(
                      (col) => (
                        <th
                          key={col}
                          className={`px-3 py-2 border-b-2 border-border text-xs font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap ${col === 'Amount' ? 'text-right' : 'text-left'}`}
                        >
                          {col === 'Next Run' ? (
                            <span className="inline-flex items-center gap-1">
                              {col}
                              <HelpTooltip text="The date when the next expense entry will be automatically created from this template." />
                            </span>
                          ) : col}
                        </th>
                      )
                    )}
                  </tr>
                </thead>
                <tbody>
                  {templates.map((template) => (
                    <React.Fragment key={template.id}>
                      <tr
                        className={`cursor-pointer ${expandedId === template.id ? 'bg-accent/50 border-b-0' : 'border-b border-border'}`}
                        onClick={() => setExpandedId((prev) => (prev === template.id ? null : template.id))}
                        tabIndex={0}
                        role="button"
                        aria-expanded={expandedId === template.id}
                        aria-label={`${template.description} — click to ${expandedId === template.id ? 'collapse' : 'expand'} history`}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setExpandedId((prev) => (prev === template.id ? null : template.id));
                          }
                        }}
                      >
                        <td className="px-3 py-3 text-foreground font-medium">
                          {template.description}
                          {template.vendorName && (
                            <span className="text-xs text-muted-foreground block font-normal">
                              {template.vendorName}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-3 text-right font-semibold text-foreground whitespace-nowrap">
                          {formatAmount(template.amount)}
                        </td>
                        <td className="px-3 py-3 text-foreground">
                          {FREQUENCY_OPTIONS.find((o) => o.value === template.frequency)?.label ?? template.frequency}
                        </td>
                        <td className="px-3 py-3 text-foreground whitespace-nowrap">
                          {formatExpenseDate(template.nextRunDate)}
                        </td>
                        <td className="px-3 py-3">
                          <StatusBadge status={template.status} id={template.id} />
                        </td>
                        <td
                          className="px-3 py-3"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="flex gap-2 flex-nowrap">
                            {template.status !== 'CANCELLED' && (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => handleOpenEdit(template)}
                                data-testid={`recurring-edit-${template.id}`}
                                aria-label={`Edit ${template.description}`}
                              >
                                Edit
                              </Button>
                            )}
                            {template.status === 'ACTIVE' && (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => handleAction(template.id, 'pause')}
                                disabled={actionLoading === `${template.id}-pause`}
                                data-testid={`recurring-pause-${template.id}`}
                                aria-label={`Pause ${template.description}`}
                              >
                                {actionLoading === `${template.id}-pause` ? '...' : 'Pause'}
                              </Button>
                            )}
                            {template.status === 'PAUSED' && (
                              <Button
                                type="button"
                                size="sm"
                                onClick={() => handleAction(template.id, 'resume')}
                                disabled={actionLoading === `${template.id}-resume`}
                                data-testid={`recurring-resume-${template.id}`}
                                aria-label={`Resume ${template.description}`}
                              >
                                {actionLoading === `${template.id}-resume` ? '...' : 'Resume'}
                              </Button>
                            )}
                            {template.status !== 'CANCELLED' && (
                              <Button
                                type="button"
                                variant="destructive"
                                size="sm"
                                onClick={() => handleAction(template.id, 'cancel')}
                                disabled={actionLoading === `${template.id}-cancel`}
                                data-testid={`recurring-cancel-${template.id}`}
                                aria-label={`Cancel ${template.description}`}
                              >
                                {actionLoading === `${template.id}-cancel` ? '...' : 'Cancel'}
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {expandedId === template.id && (
                        <tr className="border-b border-border">
                          <td
                            colSpan={6}
                            className="bg-accent/50 p-0"
                          >
                            <ExpandedTemplateRow templateId={template.id} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        title={editingTemplate ? 'Edit Recurring Expense' : 'Create Recurring Expense'}
        width="medium"
      >
        {formError && (
          <div
            role="alert"
            className="p-3 mb-4 bg-destructive/5 border border-destructive/20 rounded-md text-destructive text-sm"
          >
            {formError}
          </div>
        )}
        <RecurringExpenseForm
          form={formState}
          onChange={(updates) => setFormState((prev) => ({ ...prev, ...updates }))}
          onSubmit={handleFormSubmit}
          onCancel={() => setIsFormOpen(false)}
          isLoading={formSubmitting}
          categories={categories}
          mode={editingTemplate ? 'edit' : 'create'}
        />
      </Modal>
    </div>
    </ExpensesLayout>
  );
}

export default RecurringExpensesPage;
